import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Settings, BarChart3, FileText, Save } from 'lucide-react';
import { useForecastData } from '../hooks/useForecast';
import ForecastParametersTab from '../components/forecast/ForecastParametersTab';
import ForecastDimensionsTab from '../components/forecast/ForecastDimensionsTab';
import ForecastLinesTab from '../components/forecast/ForecastLinesTab';

type Tab = 'parameters' | 'dimensions' | 'lines';

export default function ForecastEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const forecastData = useForecastData(id || null);
  const [activeTab, setActiveTab] = useState<Tab>('lines');

  if (forecastData.loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 sm:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="text-gray-500">Chargement...</div>
          </div>
        </div>
      </div>
    );
  }

  if (!forecastData.scenario) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 sm:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="text-red-600">Scénario non trouvé</div>
          </div>
        </div>
      </div>
    );
  }

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'lines', label: 'Lignes budgétaires', icon: <FileText size={18} /> },
    { id: 'dimensions', label: 'Dimensions', icon: <BarChart3 size={18} /> },
    { id: 'parameters', label: 'Paramètres', icon: <Settings size={18} /> }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/app/forecast')}
                className="text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft size={24} />
              </button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                  {forecastData.scenario.name}
                </h1>
                <p className="text-sm text-gray-600">Édition du scénario</p>
              </div>
            </div>
            <button
              onClick={() => navigate(`/app/forecast/${id}`)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Save size={18} />
              Voir le résultat
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="border-b border-gray-200">
            <nav className="flex overflow-x-auto">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 sm:px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                  }`}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-4 sm:p-6">
            {activeTab === 'parameters' && <ForecastParametersTab forecastData={forecastData} />}
            {activeTab === 'dimensions' && <ForecastDimensionsTab forecastData={forecastData} />}
            {activeTab === 'lines' && <ForecastLinesTab forecastData={forecastData} />}
          </div>
        </div>
      </div>
    </div>
  );
}
